import { api } from "../api";
import { lineChart } from "../chart";

interface UsageRow {
  date: string;
  surface: string;
  client_id: string;
  operation: string;
  requests: number;
  successes: number;
  errors: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  tool_calls: number;
  unique_subjects: number;
}

export async function renderDashboard(el: HTMLElement): Promise<void> {
  el.innerHTML = `<h1>Dashboard</h1><div class="empty">Загрузка…</div>`;
  const [s, d, usage, h] = await Promise.all([
    api.summary(), api.daily(14), api.usage(30), api.health(),
  ]);
  const totals = aggregateUsage(usage.usage || []);
  el.innerHTML = `
    <h1>Dashboard</h1>
    <div class="cards">
      ${card("Всего юзеров", s.total_users)}
      ${card("Premium", s.premium_users)}
      ${card("Запросов сегодня", s.today_requests)}
      ${card("Cost сегодня", "$" + Number(s.today_cost_usd || 0).toFixed(4))}
      ${card("DAU сегодня", s.today_dau)}
    </div>
    <div class="cards" style="margin-top:16px">
      ${card("Mongo", h.mongo, h.mongo === "ok" ? "ok" : "danger")}
      ${card("Redis", h.redis, h.redis === "ok" ? "ok" : "danger")}
      ${card("LLM API", String(h.llm_api).slice(0, 40), h.llm_api === "ok" ? "ok" : "danger")}
    </div>
    <section class="chart card">
      <h3>Использование сегодня</h3>
      ${surfaceTable(s.today_by_surface || [])}
    </section>
    <section class="chart card">
      <h3>Каналы, клиенты и операции за 30 дней</h3>
      ${usageTable(totals)}
    </section>
    <div class="chart card">
      <h3>Запросы по дням</h3>
      ${lineChart(
        d.daily.map((x: any) => ({ label: x.date.slice(5), value: x.requests || 0 })),
        { color: "#818cf8", format: v => formatInt(v), unit: "req" }
      )}
    </div>
    <div class="chart card">
      <h3>Cost (USD) по дням</h3>
      ${lineChart(
        d.daily.map((x: any) => ({ label: x.date.slice(5), value: x.cost_usd || 0 })),
        { color: "#2dd4bf", format: v => "$" + v.toFixed(2), unit: "USD" }
      )}
    </div>
    <div class="chart card">
      <h3>DAU по дням</h3>
      ${lineChart(
        d.daily.map((x: any) => ({ label: x.date.slice(5), value: x.dau || 0 })),
        { color: "#f472b6", format: v => formatInt(v), unit: "uniq" }
      )}
    </div>
  `;
}

function aggregateUsage(rows: UsageRow[]): UsageRow[] {
  const merged = new Map<string, UsageRow>();
  for (const row of rows) {
    const key = `${row.surface}\u0000${row.client_id}\u0000${row.operation}`;
    const current = merged.get(key) || {
      ...row,
      date: "",
      requests: 0,
      successes: 0,
      errors: 0,
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
      tool_calls: 0,
      unique_subjects: 0,
    };
    for (const field of [
      "requests", "successes", "errors", "tokens_in", "tokens_out",
      "cost_usd", "tool_calls", "unique_subjects",
    ] as const) {
      current[field] += Number(row[field] || 0);
    }
    merged.set(key, current);
  }
  return [...merged.values()].sort((a, b) => b.requests - a.requests);
}

function surfaceTable(rows: any[]): string {
  if (!rows.length) return `<div class="empty">Пока нет событий</div>`;
  return `
    <div class="table-wrap"><table>
      <thead><tr><th>Канал</th><th>Запросы</th><th>Ошибки</th><th>Уникальные</th><th>Тулзы</th><th>Cost</th></tr></thead>
      <tbody>${rows.map(row => `
        <tr>
          <td><code>${esc(row.surface)}</code></td>
          <td>${formatInt(row.requests)}</td>
          <td>${formatInt(row.errors)}</td>
          <td>${formatInt(row.unique_subjects)}</td>
          <td>${formatInt(row.tool_calls)}</td>
          <td>$${Number(row.cost_usd || 0).toFixed(4)}</td>
        </tr>
      `).join("")}</tbody>
    </table></div>
  `;
}

function usageTable(rows: UsageRow[]): string {
  if (!rows.length) return `<div class="empty">Пока нет событий</div>`;
  return `
    <div class="table-wrap"><table>
      <thead><tr><th>Канал</th><th>Client ID</th><th>Операция</th><th>Запросы</th><th>Ошибки</th><th>Токены</th><th>Тулзы</th><th>Cost</th></tr></thead>
      <tbody>${rows.slice(0, 100).map(row => `
        <tr>
          <td><code>${esc(row.surface)}</code></td>
          <td><code>${esc(row.client_id)}</code></td>
          <td><code>${esc(row.operation)}</code></td>
          <td>${formatInt(row.requests)}</td>
          <td>${formatInt(row.errors)}</td>
          <td>${formatInt(row.tokens_in + row.tokens_out)}</td>
          <td>${formatInt(row.tool_calls)}</td>
          <td>$${Number(row.cost_usd || 0).toFixed(4)}</td>
        </tr>
      `).join("")}</tbody>
    </table></div>
  `;
}

function card(label: string, value: any, tag: "ok" | "warn" | "danger" | "" = ""): string {
  return `
    <div class="card">
      <div class="label">${label}</div>
      <div class="value ${tag ? `tag ${tag}` : ""}">${value}</div>
    </div>
  `;
}

function formatInt(v: number): string {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(Math.round(v || 0));
}

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, char => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]!
  ));
}
